import { useId, type InputHTMLAttributes, type ReactNode } from 'react';
import './Input.css';

export interface InputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'id'> {
  label: string;
  /** Guidance shown under the field; announced with the input. */
  hint?: ReactNode;
  /** Error text. Presence switches the field into its error state. */
  error?: string;
  /** Text rendered immediately before the value, e.g. "@" for a handle. */
  prefix?: string;
}

/**
 * Labelled text input.
 *
 * The label is always a real `<label>` — never a placeholder standing in for
 * one — and errors are tied to the input through `aria-describedby`, so screen
 * readers announce the problem with the field (section 12).
 */
export function Input({ label, hint, error, prefix, className, ...rest }: InputProps) {
  const id = useId();
  const hintId = `${id}-hint`;
  const errorId = `${id}-error`;

  const describedBy = [hint ? hintId : null, error ? errorId : null].filter(Boolean).join(' ');

  return (
    <div className={['sc-field', className ?? ''].filter(Boolean).join(' ')}>
      <label className="sc-field__label" htmlFor={id}>
        {label}
      </label>

      <div className={`sc-field__control ${error ? 'sc-field__control--error' : ''}`}>
        {prefix && (
          <span className="sc-field__prefix" aria-hidden="true">
            {prefix}
          </span>
        )}
        <input
          {...rest}
          id={id}
          className="sc-field__input"
          aria-invalid={error ? true : undefined}
          aria-describedby={describedBy || undefined}
        />
      </div>

      {hint && (
        <p className="sc-field__hint" id={hintId}>
          {hint}
        </p>
      )}
      {error && (
        <p className="sc-field__error" id={errorId}>
          {/* Icon plus text: the red border is never the only signal. */}
          <span aria-hidden="true">⚠️ </span>
          {error}
        </p>
      )}
    </div>
  );
}
